import { LoadingOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Row, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { updateUserListValue } from "../../../features/user/userListSlice";
import { postUserAsync, updateUser } from "../../../services/userAPI";
import {
  AdminCreate,
  AdministratorIdDescription,
  AdminUpdate,
  AndroidDescription,
  CASHDescription,
  DepartmentIdDescription,
  EmailDescription,
  FirstNameDescription,
  LastNameDescription,
  ManualDescription,
  MobileDescription,
  PasswordDescription,
  PasswordRegexExpression,
  PasswordRequirementText,
  SuperAdminTypeId,
} from "../../../shared/constants/Resources";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import UserProfileDTO from "../../../shared/dtos/userProfileDTO";
import "../../admin/list/styles.css";
import { departmentsByAdminAsync } from "../../../features/departments/deparmentsByAdminSlice";
import ContainerLayout from "../../common/containerLayout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectCurrentAdminType } from "../../../utils/helpers/selectorHelper";
import AdminDTO from "../../../shared/dtos/adminDTO";
import { getAllAdmins } from "../../../services/adminApi";
import { getUserById } from "../../../services/adminUserApi";
import UserDTO from "../../../shared/dtos/userDTO";
import { RootState } from "../../../app/store";

const { Title } = Typography;

export const UserForm = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserDTO | null>(null);
  const [form] = Form.useForm();
  const [admins, setAdmins] = useState<AdminDTO[]>([]);
  const adminType = selectCurrentAdminType();
  const { value: departments } = useAppSelector(
    (state: RootState) => state.departmentsByAdmin,
  );

  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (id) {
          if (adminType === SuperAdminTypeId) {
            const admins = await getAllAdmins();
            setAdmins(admins || []);
          }
          const user = await getUserById(Number(id));
          form.setFieldsValue({
            [AdministratorIdDescription]: user?.administrator,
            [DepartmentIdDescription]: user?.departmentId,
          });
          setUser(user);
          if (adminType === SuperAdminTypeId && user?.administrator) {
            dispatch(departmentsByAdminAsync(user.administrator));
          } else {
            dispatch(departmentsByAdminAsync());
          }
        } else {
          dispatch(departmentsByAdminAsync());
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const onFinish = async (values: any) => {
    const userProfileDTO: UserProfileDTO = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      mobile: values.mobile,
      departmentId: values.departmentId.value
        ? values.departmentId.value
        : values.departmentId,
      administrator: values.administrator,
    };

    if (id) {
      await updateUser(+id, userProfileDTO);
    } else {
      userProfileDTO.password = values.password;
      userProfileDTO.loginBy = ManualDescription;
      userProfileDTO.paymentMode = CASHDescription;
      userProfileDTO.deviceType = AndroidDescription;
      await postUserAsync(userProfileDTO);
    }

    dispatch(updateUserListValue());
    navigate(WebRoutes.UserTableRoute, {
      replace: true,
    });
    setUser(null);
    form.resetFields();
  };

  return (
    <ContainerLayout
      showNotification={false}
      title={`${user ? AdminUpdate : AdminCreate} usuario ${
        user?.firstName ?? ""
      } ${user?.lastName ?? ""}`}
      justify="center"
    >
      {isLoading && (
        <Col span={24} className="loading-container error-container">
          <LoadingOutlined />
        </Col>
      )}
      {!isLoading && (
        <Col span={12}>
          <Form
            layout="vertical"
            form={form}
            name="user"
            onFinish={onFinish}
            scrollToFirstError
            fields={[
              {
                name: [FirstNameDescription],
                value: user ? user?.firstName : "",
              },
              {
                name: [LastNameDescription],
                value: user ? user?.lastName : "",
              },
              {
                name: [EmailDescription],
                value: user ? user?.email : "",
              },
              {
                name: [MobileDescription],
                value: user ? user?.mobile : "",
              },
            ]}
          >
            <Row gutter={10}>
              <Col span={12}>
                <Title level={2}>Información del usuario</Title>
                <Form.Item
                  name={FirstNameDescription}
                  label="Primer Nombre"
                  tooltip="Cual es su primer nombre?"
                  rules={[
                    {
                      required: true,
                      message: "Por favor inserte su nombre!",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input placeholder="Pepe" />
                </Form.Item>
                <Form.Item
                  name={LastNameDescription}
                  label="Primer Apellido"
                  tooltip="Cual es su primer apellido?"
                  rules={[
                    {
                      required: true,
                      message: "Por favor inserte su nombre!",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input placeholder="Ruiz" />
                </Form.Item>
                <Form.Item
                  name={EmailDescription}
                  label="Correo electronico"
                  tooltip="Cual es su correo electronico?"
                  rules={[
                    {
                      required: true,
                      type: EmailDescription,
                      message: "Inserte un correo válido!",
                    },
                  ]}
                >
                  <Input placeholder="prueba@mowi.com" />
                </Form.Item>
                <Form.Item
                  name={MobileDescription}
                  label="Teléfono"
                  tooltip="Cual es su teléfono?"
                  rules={[
                    {
                      required: true,
                      message: "Inserte un número telefónico",
                    },
                  ]}
                >
                  <Input placeholder="52013994" />
                </Form.Item>
                <Form.Item
                  name={DepartmentIdDescription}
                  label="Departamento"
                  tooltip="A que departamento pertenece?"
                  rules={[
                    {
                      required: true,
                      message: "Por favor selecciona un Departamento!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Seleccione el departamento"
                    showSearch
                    optionFilterProp="label"
                    notFoundContent={null}
                    options={departments.map((x) => ({
                      value: x.id,
                      label: x.name,
                    }))}
                  />
                </Form.Item>
                {user ? null : (
                  <Form.Item
                    name={PasswordDescription}
                    label="Contraseña"
                    tooltip="Cual es su contraseña?"
                    rules={[
                      {
                        required: true,
                        message: "Por favor agregar una contraseña",
                      },
                      {
                        pattern: PasswordRegexExpression,
                        message: PasswordRequirementText,
                      },
                    ]}
                  >
                    <Input.Password placeholder="*******" />
                  </Form.Item>
                )}
                {adminType === SuperAdminTypeId && id && (
                  <Form.Item
                    name={AdministratorIdDescription}
                    label="Administrador"
                    tooltip="A que administrador pertenece?"
                    rules={[
                      {
                        required: true,
                        message: "Por favor selecciona un administrador!",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Seleccione un administrador"
                      options={admins.map((admin: AdminDTO) => ({
                        label: admin?.name,
                        value: admin?.id,
                      }))}
                      showSearch
                      optionFilterProp="label"
                      notFoundContent={null}
                    />
                  </Form.Item>
                )}
                <Space>
                  <Button type="primary" htmlType="submit">
                    {user ? AdminUpdate : AdminCreate}
                  </Button>
                  <Button
                    onClick={() =>
                      navigate(WebRoutes.UserTableRoute, {
                        replace: true,
                      })
                    }
                  >
                    Cancelar
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Col>
      )}
    </ContainerLayout>
  );
};

export default UserForm;
